// Helper to log inventory movement to Firestore history subcollection
/**
 * Log an inventory movement to Firestore history subcollection
 * @param {string} clientId
 * @param {string} materialId
 * @param {object} logData - { action, qty, note, location, category, price, weight, uom, status, material_no, ... }
 */
export async function logInventoryMovement(clientId, materialId, logData) {
  if (!clientId || !materialId) return;
  const historyRef = collection(db, "activityLogs");
  await addDoc(historyRef, {
    ...logData,
    target: "inventory",
    materialId,
    companyId: clientId,
    timestamp: serverTimestamp(),
  });
}
// src/client/inventory/useInventory.jsx
import { useEffect, useState, useRef } from 'react';
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  updateDoc,
  onSnapshot,
  query,
  where,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import useCurrentUser from '../../hooks/useCurrentUser';
import { logHistory } from '../../utils/logHistory';
// Import the logInventoryMovement helper from this file

const defaultFields = [
  'Material No',
  'Description',
  'Qty',
  'Weight',
  'UOM',
  'Location',
  'Category',
  'Price',
  'Status',
];

export default function useInventory() {
  const { clientId, role, uid } = useCurrentUser();
  const [columns] = useState([...defaultFields]);
  const [materials, setMaterials] = useState([]);
  const [newRow, setNewRow] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editedRow, setEditedRow] = useState({});
  const [transfer, setTransfer] = useState({ materialId: '', to: '', qty: 0 });
  const [blockQty, setBlockQty] = useState({ materialId: '', qty: 0 });
  const [reserveQty, setReserveQty] = useState({ materialId: '', qty: 0 });
  // Inventory search state
  const [search, setSearch] = useState("");

  const backfillDone = useRef(false);

  useEffect(() => {
    if (!clientId) return;
    const q = query(collection(db, 'inventories'), where("companyId", "==", clientId));
    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((d) => ({ id: d.id, values: d.data() }));
      setMaterials(data);

      // Backfill existing items to Master Data — runs once per session only
      if (!backfillDone.current && snapshot.docs.length > 0) {
        backfillDone.current = true;
        (async () => {
          for (const d of snapshot.docs) {
            const v = d.data();
            if (!v.materialNo && !v.description) continue;
            try {
              const productDocId = `${clientId}_${(v.materialNo || d.id).replace(/\s+/g, "_")}`;
              await setDoc(
                doc(db, "products", productDocId),
                {
                  "Material ID": v.materialNo || "",
                  "Description": v.description || "",
                  "SKU": v.materialNo || "",
                  "Price": v.price || "",
                  "Weight": v.weight || "",
                  companyId: clientId,
                  sourceInventoryId: d.id,
                },
                { merge: true }
              );
            } catch (e) {
              // Silently skip if rules block write (e.g. impersonation mode)
              console.warn("Master Data sync skipped:", e.message);
            }
          }
        })();
      }
    });
    return () => unsub();
  }, [clientId]);

  // Filtered materials by search (materialNo or description)
  const filteredMaterials = search.trim()
    ? materials.filter((m) => {
      const desc = (m.values.description || "").toLowerCase();
      const matno = (m.values.materialNo || m.values.material_no || "").toLowerCase();
      const s = search.toLowerCase();
      return desc.includes(s) || matno.includes(s);
    })
    : materials;

  const normalizeKey = (str) => {
    // Custom mapping for complex columns
    if (str === 'Material No') return 'materialNo';
    if (str === 'UOM') return 'uom';

    // Default to camelCase-ish matching field names from seeder
    return str.toLowerCase();
  };

  const handleRowChange = (e, setter) => {
    const { name, value } = e.target;
    setter((prev) => ({ ...prev, [name]: value }));
  };

  const syncToMasterData = async (payload, inventoryId) => {
    try {
      const productDocId = `${clientId}_${(payload.materialNo || inventoryId).replace(/\s+/g, "_")}`;
      await setDoc(
        doc(db, "products", productDocId),
        {
          "Material ID": payload.materialNo || "",
          "Description": payload.description || "",
          "SKU": payload.materialNo || "",
          "Price": payload.price || "",
          "Weight": payload.weight || "",
          companyId: clientId,
          sourceInventoryId: inventoryId,
          syncedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (e) {
      console.warn("Master Data sync skipped:", e.message);
    }
  };

  const addMaterial = async () => {
    const payload = {};
    columns.forEach((col) => {
      const key = normalizeKey(col);
      payload[key] = newRow[col] || '';
    });

    const ref = doc(collection(db, 'inventories'));
    await setDoc(ref, {
      ...payload,
      companyId: clientId,
      createdAt: serverTimestamp(),
    });

    // Auto-sync new material to Master Data → Products tab
    await syncToMasterData(payload, ref.id);

    await logHistory({
      clientId,
      docId: ref.id,
      user: { uid, role },
      action: 'MATERIAL_ADDED',
      note: `Added material ${payload.material_no || '(unknown)'}`,
      type: 'inventory',
      details: payload,
    });

    // Log to Firestore history
    await logInventoryMovement(clientId, ref.id, {
      action: 'MATERIAL_ADDED',
      qty: payload.qty || 0,
      note: `Added material ${payload.materialNo || '(unknown)'}`,
      location: payload.location || '',
      category: payload.category || '',
      price: payload.price || '',
      weight: payload.weight || '',
      uom: payload.uom || '',
      status: payload.status || '',
      materialNo: payload.materialNo || '',
      description: payload.description || '',
    });

    setNewRow({});
  };


  const deleteRow = async (id) => {
    const prev = materials.find((m) => m.id === id);
    await deleteDoc(doc(db, 'inventories', id));

    await logHistory({
      clientId,
      docId: id,
      user: { uid, role },
      action: 'MATERIAL_DELETED',
      note: 'Deleted material',
      type: 'inventory',
      details: prev?.values || {},
    });
  };

  const startEditing = (row) => {
    setEditingId(row.id);
    setEditedRow({ ...row.values });
  };

  const saveEdit = async () => {
    const ref = doc(db, 'inventories', editingId);
    const prev = materials.find((m) => m.id === editingId);
    await updateDoc(ref, editedRow);

    // Keep Master Data in sync when material details are edited
    await syncToMasterData(editedRow, editingId);

    await logHistory({
      clientId,
      docId: editingId,
      user: { uid, role },
      action: 'MATERIAL_EDITED',
      note: 'Edited material',
      type: 'inventory',
      details: {
        before: prev?.values,
        after: editedRow,
      },
    });

    // Log to Firestore history
    const prevQty = prev?.values?.qty;
    const newQty = editedRow.qty;
    if (prevQty !== undefined && newQty !== undefined && String(prevQty) !== String(newQty)) {
      // Qty changed, log as QTY_UPDATED
      await logInventoryMovement(clientId, editingId, {
        action: 'QTY_UPDATED',
        qty: newQty,
        note: `Qty changed from ${prevQty} to ${newQty}`,
        location: editedRow.location || '',
        category: editedRow.category || '',
        price: editedRow.price || '',
        weight: editedRow.weight || '',
        uom: editedRow.uom || '',
        status: editedRow.status || '',
        materialNo: editedRow.materialNo || '',
        description: editedRow.description || '',
        beforeQty: prevQty,
        afterQty: newQty,
      });
    } else {
      // No qty change, log as edit
      await logInventoryMovement(clientId, editingId, {
        action: 'MATERIAL_EDITED',
        qty: editedRow.qty || 0,
        note: 'Edited material',
        location: editedRow.location || '',
        category: editedRow.category || '',
        price: editedRow.price || '',
        weight: editedRow.weight || '',
        uom: editedRow.uom || '',
        status: editedRow.status || '',
        materialNo: editedRow.materialNo || '',
        description: editedRow.description || '',
      });
    }

    setEditingId(null);
    setEditedRow({});
  };

  const updateQty = async (id, qtyDiff, label) => {
    const mat = materials.find((m) => m.id === id);
    if (!mat) return;
    const oldQty = parseFloat(mat.values.qty) || 0;
    if (qtyDiff > oldQty) return;
    const newData = {
      ...mat.values,
      qty: oldQty - qtyDiff,
      [`${label}_qty`]: (parseFloat(mat.values[`${label}_qty`]) || 0) + qtyDiff,
    };
    await updateDoc(doc(db, 'inventories', id), newData);

    await logHistory({
      clientId,
      docId: id,
      user: { uid, role },
      action: 'QTY_UPDATED',
      note: `${label} ${qtyDiff}`,
      type: 'inventory',
      details: {
        beforeQty: oldQty,
        afterQty: oldQty - qtyDiff,
        label,
      },
    });

    // Log to Firestore history
    await logInventoryMovement(clientId, id, {
      action: 'QTY_UPDATED',
      qty: qtyDiff,
      note: `${label} ${qtyDiff}`,
      location: mat.values.location || '',
      category: mat.values.category || '',
      price: mat.values.price || '',
      weight: mat.values.weight || '',
      uom: mat.values.uom || '',
      status: mat.values.status || '',
      materialNo: mat.values.materialNo || '',
      description: mat.values.description || '',
      label,
      beforeQty: oldQty,
      afterQty: oldQty - qtyDiff,
    });
  };

  const blockStock = async () => {
    if (!blockQty.materialId || blockQty.qty <= 0) return;
    const qty = parseFloat(blockQty.qty);
    await updateQty(blockQty.materialId, qty, 'blocked');
    setBlockQty({ materialId: '', qty: 0 });
  };

  const reserveStock = async () => {
    if (!reserveQty.materialId || reserveQty.qty <= 0) return;
    const qty = parseFloat(reserveQty.qty);
    await updateQty(reserveQty.materialId, qty, 'reserved');
    setReserveQty({ materialId: '', qty: 0 });
  };

  const totalStock = filteredMaterials.reduce((sum, m) => sum + (parseFloat(m.values.qty) || 0), 0);
  const totalValue = filteredMaterials.reduce((sum, m) => {
    const qty = parseFloat(m.values.qty) || 0;
    const price = parseFloat(m.values.price) || 0;
    return sum + qty * price;
  }, 0);

  const top10ByValue = [...filteredMaterials]
    .sort((a, b) => {
      const aVal = (parseFloat(a.values.qty) || 0) * (parseFloat(a.values.price) || 0);
      const bVal = (parseFloat(b.values.qty) || 0) * (parseFloat(b.values.price) || 0);
      return bVal - aVal;
    })
    .slice(0, 10)
    .map((mat) => ({
      name: mat.values.materialNo || mat.values.material_no || 'Unknown',
      value: (parseFloat(mat.values.qty) || 0) * (parseFloat(mat.values.price) || 0),
    }));

  return {
    columns,
    materials: filteredMaterials,
    newRow,
    editingId,
    editedRow,
    transfer,
    blockQty,
    reserveQty,
    totalStock,
    totalValue,
    top10ByValue,
    setNewRow,
    setEditedRow,
    setTransfer,
    setBlockQty,
    setReserveQty,
    addMaterial,
    deleteRow,
    startEditing,
    saveEdit,
    handleRowChange,
    blockStock,
    reserveStock,
    normalizeKey,
    setEditingId,
    // Expose search state
    search,
    setSearch,
    // Expose unfiltered materials if needed
    allMaterials: materials,
  };
}
